import { useState } from 'react';
import type { ClubTheme } from './themes';
import { useClubMembers, useCreateConversation } from './queries';

interface NewConversationProps {
  clubId: string;
  theme: ClubTheme;
  onCreated: (conversationId: string) => void;
  onCancel: () => void;
}

export default function NewConversation({ clubId, theme, onCreated, onCancel }: NewConversationProps) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  const { data: members = [], isLoading: loading } = useClubMembers(clubId);
  const createConv = useCreateConversation(clubId);

  const filtered = search.trim()
    ? members.filter((m) =>
        (m.user?.full_name || '').toLowerCase().includes(search.toLowerCase()),
      )
    : members;

  const handleStart = () => {
    if (!selectedId || createConv.isPending) return;
    createConv.mutate(
      { participant_ids: [selectedId], message: message.trim() || undefined },
      {
        onSuccess: (data) => onCreated(data.data?.conversation_id),
      },
    );
  };

  const selectedMember = members.find((m) => m.user?.id === selectedId);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div
        className="flex items-center gap-3 border-b px-4 py-3"
        style={{ borderColor: `${theme.colors.muted}20` }}
      >
        <button
          type="button"
          onClick={onCancel}
          className="text-sm font-medium"
          style={{ color: theme.colors.primary }}
        >
          Cancel
        </button>
        <h3 className="flex-1 text-sm font-semibold" style={{ color: theme.colors.text }}>
          New Message
        </h3>
      </div>

      {/* Search */}
      <div className="px-4 py-3" style={{ borderBottom: `1px solid ${theme.colors.muted}20` }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search members..."
          className="w-full rounded-xl px-3 py-2 text-sm outline-none"
          style={{
            backgroundColor: `${theme.colors.muted}10`,
            color: theme.colors.text,
            border: `1px solid ${theme.colors.muted}25`,
          }}
        />
      </div>

      {/* Member list */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <p className="px-4 py-6 text-center text-xs" style={{ color: theme.colors.muted }}>
            Loading members...
          </p>
        )}

        {filtered.map((m) => {
          const isSelected = m.user?.id === selectedId;
          return (
            <button
              key={m.user?.id}
              type="button"
              onClick={() => setSelectedId(m.user?.id || null)}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors"
              style={{
                backgroundColor: isSelected ? `${theme.colors.primary}15` : 'transparent',
              }}
            >
              {m.user?.avatar_url ? (
                <img src={m.user.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
              ) : (
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold"
                  style={{ backgroundColor: `${theme.colors.primary}25`, color: theme.colors.primary }}
                >
                  {(m.user?.full_name?.[0] || '?').toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <span className="text-sm truncate block" style={{ color: theme.colors.text }}>
                  {m.user?.full_name || 'Unknown'}
                </span>
                <span className="text-[10px] capitalize" style={{ color: theme.colors.muted }}>
                  {m.club_role}
                </span>
              </div>
              {isSelected && (
                <div
                  className="flex h-5 w-5 items-center justify-center rounded-full"
                  style={{ backgroundColor: theme.colors.primary }}
                >
                  <svg className="h-3 w-3" viewBox="0 0 20 20" fill={theme.colors.bg}>
                    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Compose + send */}
      {selectedId && (
        <div className="border-t px-4 py-3 space-y-2" style={{ borderColor: `${theme.colors.muted}20` }}>
          <p className="text-xs" style={{ color: theme.colors.muted }}>
            To: <span style={{ color: theme.colors.text, fontWeight: 600 }}>
              {selectedMember?.user?.full_name || 'Unknown'}
            </span>
          </p>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your first message (optional)..."
            rows={2}
            className="w-full resize-none rounded-xl px-3 py-2 text-sm outline-none"
            style={{
              backgroundColor: `${theme.colors.muted}10`,
              color: theme.colors.text,
              border: `1px solid ${theme.colors.muted}25`,
            }}
          />
          {createConv.error && <p className="text-xs text-red-400">{createConv.error.message}</p>}
          <button
            type="button"
            onClick={handleStart}
            disabled={createConv.isPending}
            className="w-full rounded-xl py-2 text-sm font-semibold transition-opacity disabled:opacity-40"
            style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
          >
            {createConv.isPending ? 'Starting...' : 'Start Conversation'}
          </button>
        </div>
      )}
    </div>
  );
}
